import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiMapPin, FiAward, FiMessageSquare, FiCalendar } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthModal from "../components/cowork/AuthModal";
import MemberShell from "../components/cowork/MemberShell";
import Avatar from "../components/cowork/Avatar";
import { RatingText, Stars } from "../components/cowork/Stars";
import { openChat } from "../components/cowork/chatActions";
import useCoWorkUser from "../hooks/useCoWorkUser";
import useCoWorkBranding from "../hooks/useCoWorkBranding";
import { apiRequest, fetchCoWorkSettings, formatMoney } from "../api";
import { SERVICE_ICON_MAP } from "../data/serviceIcons";
import { cardClass, splitList, timeAgo } from "../components/cowork/jobUtils";

export default function CoWorkSellerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh, signOut } = useCoWorkUser();
  const [seller, setSeller] = useState(undefined);   // undefined = loading, null = not found
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    fetchCoWorkSettings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setSeller(undefined);
    apiRequest(`/core/cowork/sellers/${id}/`).then(({ ok, data }) => setSeller(ok ? data : null));
  }, [id]);

  useCoWorkBranding(settings);
  useEffect(() => {
    if (seller) document.title = `${seller.name} - ${settings?.browser_title || "Co-Work"}`;
  }, [seller, settings]);

  const currency = settings?.currency_label || "USD";

  const message = async () => {
    setNotice("");
    if (!user) return setModal(true);
    if (user.role !== "buyer") return setNotice("Only buyer accounts can start a chat with a seller.");
    const error = await openChat(seller.user_id, navigate);
    if (error) setNotice(error);
  };

  const services = seller ? splitList(seller.services).map((k) => SERVICE_ICON_MAP[k]).filter(Boolean) : [];
  const skills = seller ? splitList(seller.skills) : [];

  return (
    <>
      <Header logo={settings?.logo} />
      <div className="h-16" />
      <main className="bg-navy text-white min-h-[70vh] py-10">
        <MemberShell user={user} onSignOut={signOut}>
          <Link to="/co-work?find=1" className="inline-block text-accent-light text-sm mb-6 hover:underline">&larr; Back to sellers</Link>

          {seller === undefined && <p className="text-gray-400">Loading...</p>}
          {seller === null && (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold">Seller not found</h1>
              <p className="text-gray-400 mt-2">This profile may no longer be available.</p>
            </div>
          )}

          {seller && (
            <div className="space-y-6">
              <div className={`${cardClass} p-7`}>
                <div className="flex flex-wrap gap-6 items-start">
                  <Avatar src={seller.avatar} name={seller.name} size={110} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h1 className="text-3xl font-extrabold tracking-tight">{seller.name}</h1>
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 inline-flex items-center gap-1"><FiAward size={12} /> {seller.badge}</span>
                    </div>
                    <p className="text-lg text-gray-300 mt-1">{seller.headline || "Seller"}</p>
                    <div className="mt-2"><RatingText avg={seller.rating_avg} count={seller.rating_count} size={16} /></div>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-400">
                      {seller.country && <span className="inline-flex items-center gap-1"><FiMapPin size={13} /> {seller.country}</span>}
                      <span>{seller.jobs_done} job{seller.jobs_done === 1 ? "" : "s"} completed</span>
                      <span className="inline-flex items-center gap-1"><FiCalendar size={13} /> Member since {new Date(seller.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {seller.hourly_rate && (
                      <>
                        <p className="text-3xl font-extrabold text-accent-light">{currency} {formatMoney(seller.hourly_rate)}</p>
                        <p className="text-xs text-gray-400 mb-3">per hour</p>
                      </>
                    )}
                    <button onClick={message} className="px-6 py-2.5 rounded-full bg-brand-gradient font-semibold inline-flex items-center gap-2"><FiMessageSquare /> Message</button>
                  </div>
                </div>
                {notice && <p className="mt-4 text-sm text-amber-300">{notice}</p>}
              </div>

              <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
                <div className="space-y-6">
                  {seller.bio && (
                    <div className={`${cardClass} p-6`}>
                      <h2 className="text-lg font-bold mb-2">About</h2>
                      <p className="text-gray-300 whitespace-pre-line leading-relaxed">{seller.bio}</p>
                    </div>
                  )}
                  <div className={`${cardClass} p-6`}>
                    <h2 className="text-lg font-bold mb-4">Reviews ({seller.rating_count})</h2>
                    {seller.reviews.length === 0 ? (
                      <p className="text-gray-500 text-sm">No reviews yet. Reviews appear after a buyer completes a job with this seller.</p>
                    ) : (
                      <div className="space-y-4">
                        {seller.reviews.map((r, i) => (
                          <div key={i} className="border-b border-navy-border last:border-0 pb-4 last:pb-0">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold text-sm">{r.reviewer_name}</span>
                              <Stars value={r.rating} size={13} />
                            </div>
                            <p className="text-xs text-gray-500">{r.job_title} · {timeAgo(r.created_at)}</p>
                            {r.comment && <p className="text-sm text-gray-300 mt-2">{r.comment}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <aside className={`${cardClass} p-6 space-y-5`}>
                  {services.length > 0 && (
                    <div>
                      <h2 className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Services</h2>
                      <div className="flex flex-wrap gap-2">
                        {services.map(({ key, label, Icon }) => (
                          <span key={key} className="px-2.5 py-1.5 rounded-md bg-accent/10 border border-accent-light/25 text-accent-light text-xs inline-flex items-center gap-1.5"><Icon size={12} /> {label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skills.length > 0 && (
                    <div>
                      <h2 className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Skills</h2>
                      <div className="flex flex-wrap gap-2">
                        {skills.map((s) => <span key={s} className="px-2.5 py-1 rounded-md bg-white/5 border border-navy-border text-xs text-gray-200">{s}</span>)}
                      </div>
                    </div>
                  )}
                  {services.length === 0 && skills.length === 0 && <p className="text-sm text-gray-500">This seller hasn't listed services yet.</p>}
                </aside>
              </div>
            </div>
          )}
        </MemberShell>
      </main>
      <Footer />

      {modal && (
        <AuthModal
          initialMode="register"
          initialRole="buyer"
          settings={settings}
          onClose={() => setModal(false)}
          onAuthenticated={async () => { setModal(false); await refresh(); }}
        />
      )}
    </>
  );
}
